// src/components/billing/AdminLegalReviewPanel.tsx
/**
 * Admin Legal Review Panel — superadmin-only view of legal compliance across all organizations.
 * Fetches from GET /billing/legal/admin/compliance-overview/
 */

import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Avatar,
  Box,
  Typography,
  TextField,
  Chip,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Collapse,
  Skeleton,
  Alert,
  Button,
  Tooltip,
  alpha,
  InputAdornment,
  CircularProgress,
  useTheme,
} from "@mui/material";
import GavelIcon from "@mui/icons-material/Gavel";
import PeopleIcon from "@mui/icons-material/People";
import SearchIcon from "@mui/icons-material/Search";
import DownloadIcon from "@mui/icons-material/Download";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import RemoveIcon from "@mui/icons-material/Remove";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import VerifiedUserIcon from "@mui/icons-material/VerifiedUser";
import { useTranslation } from "../../i18n";
import { fetchData } from "../../api/apiHelpers";
import api from "../../api/api";
import { athenaColors } from "../../theme/palette";
import { glass } from "../../theme/glassTokens";


// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface OrgMember {
  id: string;
  email: string;
  name: string;
  role: string;
  accepted_at: string | null;
}

interface ComplianceOverviewResponse {
  summary: {
    total_organizations: number;
    agreements_signed: number;
    agreements_pending: number;
    agreements_not_started: number;
    consent_acknowledged: number;
    total_users: number;
  };
  organizations: Array<{
    id: string;
    name: string;
    tier: string;
    subscription_status: string;
    agreement: {
      id: string;
      status: string;
      verification_method: string;
      signed_by: { name: string; email: string } | null;
      signed_at: string | null;
      selected_modules: string[];
      document_hash: string;
      placeholder_values: Record<string, string>;
      ip_address: string;
      has_signed_pdf: boolean;
    } | null;
    consent_acknowledged: boolean;
    consent_acknowledged_at: string | null;
    consent_acknowledged_by: string;
    members: OrgMember[];
    member_count: number;
  }>;
}

type FilterStatus = "all" | "signed" | "pending" | "not_started";

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function downloadBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const AdminLegalReviewPanel: React.FC = () => {
  const theme = useTheme();
  const textPrimary = glass.textPrimary;
  const textSecondary = glass.textSecondary;
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<ComplianceOverviewResponse | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [filterStatus, setFilterStatus] = useState<FilterStatus>("all");
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  const [downloadingId, setDownloadingId] = useState<string | null>(null);

  const fetchOverview = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const resp = await fetchData<ComplianceOverviewResponse>(
        "/billing/legal/admin/compliance-overview/"
      );
      setData(resp);
    } catch (e) {
      setError(e instanceof Error ? e.message : t("admin_legal.load_error", "Failed to load compliance overview"));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchOverview();
  }, [fetchOverview]);

  const filteredOrgs = useMemo(() => {
    if (!data?.organizations) return [];
    let list = data.organizations;

    if (searchQuery.trim()) {
      const q = searchQuery.trim().toLowerCase();
      list = list.filter((o) => o.name.toLowerCase().includes(q));
    }

    if (filterStatus === "signed") {
      list = list.filter((o) => o.agreement?.status === "signed");
    } else if (filterStatus === "pending") {
      list = list.filter((o) => o.agreement && o.agreement.status !== "signed");
    } else if (filterStatus === "not_started") {
      list = list.filter((o) => !o.agreement);
    }

    return list;
  }, [data?.organizations, searchQuery, filterStatus]);

  const toggleExpand = (orgId: string) => {
    setExpandedRows((prev) => {
      const next = new Set(prev);
      if (next.has(orgId)) next.delete(orgId);
      else next.add(orgId);
      return next;
    });
  };

  const handleDownload = useCallback(async (agreementId: string, orgName: string) => {
    setDownloadingId(agreementId);
    try {
      // Direct api.get needed for blob responseType
      const resp = await api.get(
        `/billing/legal/institution/agreements/${agreementId}/download/`,
        { responseType: "blob" }
      );
      const safeName = orgName.replace(/[^a-zA-Z0-9_-]/g, "_").slice(0, 40);
      downloadBlob(resp.data as Blob, `agreement_${safeName}.pdf`);
    } catch {
      // Silently fail
    } finally {
      setDownloadingId(null);
    }
  }, []);

  const getStatusChipProps = (status: string | undefined) => {
    if (!status) return { variant: "outlined" as const, color: "default" as const };
    const s = status.toLowerCase();
    if (s === "signed") return { variant: "filled" as const, color: "success" as const };
    if (s === "draft" || s === "pending_review")
      return { variant: "filled" as const, color: "warning" as const };
    return { variant: "outlined" as const, color: "default" as const };
  };

  const formatDate = (iso: string | null) =>
    iso ? new Date(iso).toLocaleDateString(undefined, { dateStyle: "medium" }) : "—";

  if (loading) {
    return (
      <Box>
        <Skeleton variant="rectangular" height={120} sx={{ borderRadius: 2, mb: 2 }} />
        <Skeleton variant="rectangular" height={200} sx={{ borderRadius: 2 }} />
      </Box>
    );
  }

  if (error && !data) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        {error}
        <Button onClick={fetchOverview} size="small" sx={{ ml: 2 }}>
          {t("admin_legal.retry", "Retry")}
        </Button>
      </Alert>
    );
  }

  const summary = data?.summary ?? {
    total_organizations: 0,
    agreements_signed: 0,
    agreements_pending: 0,
    agreements_not_started: 0,
    consent_acknowledged: 0,
    total_users: 0,
  };

  return (
    <Box sx={{ color: textPrimary }}>
      {/* Section title */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 3 }}>
        <Box
          sx={{
            p: 1,
            borderRadius: 2,
            background: `linear-gradient(135deg, ${athenaColors.deepNavy}, ${athenaColors.royalBlue})`,
            display: "flex",
            alignItems: "center",
          }}
        >
          <GavelIcon sx={{ color: theme.palette.common.white, fontSize: 22 }} />
        </Box>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 700, color: textPrimary }}>
            {t("admin_legal.title", "Legal Compliance Review")}
          </Typography>
          <Typography variant="caption" sx={{ color: textSecondary }}>
            {t("admin_legal.subtitle", "Review agreement and consent status across all organizations")}
          </Typography>
        </Box>
      </Box>

      {/* Summary cards */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr 1fr", md: "repeat(5, 1fr)" },
          gap: 2,
          mb: 3,
        }}
      >
        {[
          { label: t("admin_legal.total_orgs", "Total Organizations"), value: summary.total_organizations, bg: glass.panel, border: glass.panelBorder, hoverBorder: glass.headerDivider },
          { label: t("admin_legal.total_users", "Total Users"), value: summary.total_users, bg: alpha(glass.accent, 0.12), border: alpha(glass.accent, 0.5), hoverBorder: alpha(glass.accent, 0.7) },
          { label: t("admin_legal.agreements_signed", "Agreements Signed"), value: summary.agreements_signed, bg: alpha(theme.palette.success.light, 0.15), border: alpha(theme.palette.success.light, 0.5), hoverBorder: alpha(theme.palette.success.light, 0.7) },
          { label: t("admin_legal.agreements_pending", "Agreements Pending"), value: summary.agreements_pending, bg: alpha(theme.palette.warning.main, 0.12), border: alpha(theme.palette.warning.main, 0.5), hoverBorder: alpha(theme.palette.warning.main, 0.7) },
          { label: t("admin_legal.consent_acknowledged", "Consent Acknowledged"), value: summary.consent_acknowledged, bg: alpha(theme.palette.success.light, 0.15), border: alpha(theme.palette.success.light, 0.5), hoverBorder: alpha(theme.palette.success.light, 0.7) },
        ].map((card) => (
          <Box
            key={card.label}
            sx={{
              p: { xs: 1.5, sm: 2.5 },
              borderRadius: 2,
              background: card.bg,
              border: `1px solid ${card.border}`,
              backdropFilter: "blur(8px)",
              transition: "border-color 0.2s ease",
              "&:hover": { borderColor: card.hoverBorder },
            }}
          >
            <Typography variant="caption" sx={{ color: textSecondary, display: "block", mb: 0.5 }}>
              {card.label}
            </Typography>
            <Typography variant="h5" sx={{ fontWeight: 700, color: textPrimary }}>
              {card.value}
            </Typography>
          </Box>
        ))}
      </Box>

      {/* Search */}
      <TextField
        fullWidth
        size="small"
        placeholder={t("admin_legal.search_placeholder", "Search by organization name...")}
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon sx={{ color: textSecondary, fontSize: 20 }} />
            </InputAdornment>
          ),
        }}
        sx={{
          mb: 2,
          "& .MuiOutlinedInput-root": {
            backgroundColor: glass.panel,
            color: textPrimary,
            "& fieldset": { borderColor: glass.panelBorder },
            "&:hover fieldset": { borderColor: glass.headerDivider },
            "&.Mui-focused fieldset": { borderColor: glass.accent },
          },
          "& .MuiInputBase-input::placeholder": {
            color: textSecondary,
            opacity: 0.7,
          },
        }}
      />

      {/* Filter chips */}
      <Box sx={{ display: "flex", gap: 1, mb: 2, flexWrap: "wrap" }}>
        {(["all", "signed", "pending", "not_started"] as const).map((f) => (
          <Chip
            key={f}
            label={t(`admin_legal.filter_${f}`, f === "all" ? "All" : f === "signed" ? "Signed" : f === "pending" ? "Pending" : "Not Started")}
            onClick={() => setFilterStatus(f)}
            sx={{
              fontWeight: 600,
              backgroundColor: filterStatus === f ? alpha(athenaColors.royalBlue, 0.15) : "transparent",
              color: filterStatus === f ? textPrimary : textSecondary,
              border: `1px solid ${filterStatus === f ? alpha(athenaColors.royalBlue, 0.4) : glass.panelBorder}`,
              "&:hover": {
                backgroundColor: alpha(athenaColors.royalBlue, 0.1),
              },
            }}
          />
        ))}
      </Box>

      {/* Table */}
      <TableContainer
        sx={{
          borderRadius: 2,
          border: `1px solid ${glass.panelBorder}`,
          background: glass.panel,
          backdropFilter: "blur(8px)",
        }}
      >
        {/* Body ink only: the head cells above already set their own, with a
            different border tone, so the full glassTableSx would flatten it. */}
        <Table size="small" sx={{ "& tbody td": { color: glass.textPrimary } }}>
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: 40, borderColor: glass.panelBorder }} />
              {[
                { key: "col_organization", label: "Organization" },
                { key: "col_tier", label: "Tier" },
                { key: "col_members", label: "Members" },
                { key: "col_agreement", label: "Agreement" },
                { key: "col_verification", label: "Verification" },
                { key: "col_signed_by", label: "Signed By" },
                { key: "col_date", label: "Date" },
                { key: "col_consent", label: "Consent" },
                { key: "col_actions", label: "Actions" },
              ].map((col) => (
                <TableCell
                  key={col.key}
                  sx={{
                    color: textSecondary,
                    fontWeight: 700,
                    fontSize: "0.75rem",
                    textTransform: "uppercase",
                    borderColor: glass.panelBorder,
                    ...(col.key === "col_actions" && { width: 60 }),
                  }}
                >
                  {t(`admin_legal.${col.key}`, col.label)}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredOrgs.map((org) => {
              const agr = org.agreement;
              const hasExpandable = org.members?.length > 0 || (agr && (Object.keys(agr.placeholder_values || {}).length > 0 || agr.document_hash || agr.ip_address));
              const isExpanded = expandedRows.has(org.id);

              const getRoleColor = (role: string) => {
                if (role === "owner") return "error";
                if (role === "admin") return "warning";
                return "default";
              };

              return (
                <React.Fragment key={org.id}>
                  <TableRow
                    sx={{
                      cursor: hasExpandable ? "pointer" : "default",
                      "&:hover": { backgroundColor: glass.rowDivider },
                      "& td": { borderColor: glass.rowDivider, color: textPrimary },
                    }}
                    onClick={() => hasExpandable && toggleExpand(org.id)}
                  >
                    <TableCell sx={{ py: 0.5, width: 40 }}>
                      {hasExpandable ? (
                        <IconButton size="small" sx={{ color: textSecondary }}>
                          {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                        </IconButton>
                      ) : null}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 600, color: textPrimary }}>{org.name}</Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={org.tier}
                        sx={{
                          fontSize: "0.7rem",
                          backgroundColor: alpha(athenaColors.royalBlue, 0.12),
                          color: glass.accent,
                          border: `1px solid ${alpha(athenaColors.royalBlue, 0.3)}`,
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                        <PeopleIcon sx={{ fontSize: 16, color: textSecondary }} />
                        <Typography variant="body2" sx={{ color: textPrimary }}>{org.member_count}</Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      {agr ? (
                        <Chip
                          size="small"
                          label={agr.status.replace(/_/g, " ")}
                          {...getStatusChipProps(agr.status)}
                          sx={{
                            ...(agr.status?.toLowerCase() === "signed" && {
                              backgroundColor: alpha(theme.palette.success.light, 0.15),
                              color: theme.palette.success.light,
                              border: `1px solid ${alpha(theme.palette.success.light, 0.4)}`,
                            }),
                            ...((agr.status === "draft" || agr.status === "pending_review") && {
                              backgroundColor: alpha(theme.palette.warning.main, 0.12),
                              color: theme.palette.warning.main,
                              border: `1px solid ${alpha(theme.palette.warning.main, 0.4)}`,
                            }),
                            ...(!["signed", "draft", "pending_review"].includes(agr.status?.toLowerCase() || "") && {
                              borderColor: glass.panelBorder,
                              color: textSecondary,
                            }),
                          }}
                        />
                      ) : (
                        <Chip
                          size="small"
                          variant="outlined"
                          label={t("admin_legal.not_started", "Not started")}
                          sx={{
                            borderColor: glass.panelBorder,
                            color: textSecondary,
                          }}
                        />
                      )}
                    </TableCell>
                    <TableCell>
                      {agr?.verification_method ? (
                        <Chip
                          size="small"
                          icon={<VerifiedUserIcon sx={{ fontSize: 14 }} />}
                          label={agr.verification_method.replace(/_/g, " ")}
                          sx={{
                            fontSize: "0.7rem",
                            backgroundColor: alpha(athenaColors.royalBlue, 0.1),
                            color: textPrimary,
                            border: `1px solid ${alpha(athenaColors.royalBlue, 0.2)}`,
                            "& .MuiChip-icon": { color: "inherit" },
                          }}
                        />
                      ) : (
                        <Typography variant="body2" sx={{ color: textSecondary }}>—</Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      {agr?.signed_by ? (
                        <Tooltip title={agr.signed_by.email}>
                          <Typography variant="body2" noWrap sx={{ maxWidth: 160, color: textPrimary }}>
                            {agr.signed_by.name || agr.signed_by.email}
                          </Typography>
                        </Tooltip>
                      ) : <Typography variant="body2" sx={{ color: textSecondary }}>—</Typography>}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ color: textPrimary }}>{formatDate(agr?.signed_at ?? null)}</Typography>
                    </TableCell>
                    <TableCell>
                      {org.consent_acknowledged ? (
                        <CheckCircleIcon sx={{ color: theme.palette.success.light, fontSize: 20 }} />
                      ) : (
                        <RemoveIcon sx={{ color: textSecondary, opacity: 0.5, fontSize: 20 }} />
                      )}
                    </TableCell>
                    <TableCell onClick={(e) => e.stopPropagation()}>
                      {agr?.has_signed_pdf ? (
                        <Tooltip title={t("admin_legal.download_pdf", "Download PDF")}>
                          <IconButton
                            size="small"
                            onClick={() => handleDownload(agr.id, org.name)}
                            disabled={downloadingId === agr.id}
                            sx={{ color: glass.accent }}
                          >
                            {downloadingId === agr.id ? (
                              <CircularProgress size={18} sx={{ color: "inherit" }} />
                            ) : (
                              <DownloadIcon fontSize="small" />
                            )}
                          </IconButton>
                        </Tooltip>
                      ) : (
                        <Typography variant="body2" sx={{ color: textSecondary }}>—</Typography>
                      )}
                    </TableCell>
                  </TableRow>
                  {hasExpandable && (
                    <TableRow>
                      <TableCell colSpan={10} sx={{ py: 0, borderColor: glass.rowDivider, borderBottom: "none" }}>
                        <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                          <Box sx={{ p: 2, backgroundColor: glass.rowDivider, borderRadius: 1 }}>
                            {/* Members */}
                            {org.members?.length > 0 && (
                              <Box sx={{ mb: agr?.document_hash || agr?.ip_address ? 2 : 0 }}>
                                <Typography variant="caption" sx={{ color: textSecondary, fontWeight: 700, display: "block", mb: 1 }}>
                                  {t("admin_legal.members_heading", "Members")} ({org.members.length})
                                </Typography>
                                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                                  {org.members.map((m) => (
                                    <Box
                                      key={m.id}
                                      sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 1,
                                        px: 1.5,
                                        py: 0.75,
                                        borderRadius: 1.5,
                                        backgroundColor: glass.panel,
                                        border: `1px solid ${glass.panelBorder}`,
                                      }}
                                    >
                                      <Avatar
                                        sx={{
                                          width: 24,
                                          height: 24,
                                          fontSize: "0.7rem",
                                          bgcolor: alpha(athenaColors.royalBlue, 0.15),
                                          color: glass.accent,
                                        }}
                                      >
                                        {(m.name[0] || m.email[0]).toUpperCase()}
                                      </Avatar>
                                      <Box>
                                        <Typography variant="caption" sx={{ display: "block", fontWeight: 600, color: textPrimary, lineHeight: 1.2 }}>
                                          {m.name}
                                        </Typography>
                                        <Typography variant="caption" sx={{ display: "block", color: textSecondary, fontSize: "0.65rem", lineHeight: 1.2 }}>
                                          {m.email}
                                        </Typography>
                                      </Box>
                                      <Chip
                                        size="small"
                                        label={m.role}
                                        color={getRoleColor(m.role) as any}
                                        variant="outlined"
                                        sx={{ height: 20, fontSize: "0.65rem", ml: 0.5 }}
                                      />
                                    </Box>
                                  ))}
                                </Box>
                              </Box>
                            )}

                            {/* Audit details */}
                            {(agr?.document_hash || agr?.ip_address) && org.members?.length > 0 && (
                              <Divider sx={{ borderColor: glass.panelBorder, mb: 1.5 }} />
                            )}
                            {agr?.document_hash && (
                              <Typography variant="caption" sx={{ display: "block", color: textSecondary, mb: 0.5 }}>
                                <strong>{t("admin_legal.document_hash", "Document hash")}:</strong> <code>{agr.document_hash}</code>
                              </Typography>
                            )}
                            {agr?.ip_address && (
                              <Typography variant="caption" sx={{ display: "block", color: textSecondary, mb: 0.5 }}>
                                <strong>{t("admin_legal.ip_address", "IP address")}:</strong> {agr.ip_address}
                              </Typography>
                            )}
                            {agr?.placeholder_values && Object.keys(agr.placeholder_values).length > 0 && (
                              <Box sx={{ mt: 1 }}>
                                <Typography variant="caption" sx={{ color: textSecondary, fontWeight: 700, display: "block", mb: 0.5 }}>
                                  {t("admin_legal.placeholder_values", "Placeholder values")}:
                                </Typography>
                                <Box component="pre" sx={{ fontSize: "0.75rem", color: textPrimary, m: 0, fontFamily: "monospace", whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
                                  {JSON.stringify(agr.placeholder_values, null, 2)}
                                </Box>
                              </Box>
                            )}
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  )}
                </React.Fragment>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      {filteredOrgs.length === 0 && (
        <Typography variant="body2" sx={{ color: textSecondary, textAlign: "center", py: 4 }}>
          {t("admin_legal.no_results", "No organizations match your filters.")}
        </Typography>
      )}
    </Box>
  );
};

export default AdminLegalReviewPanel;
